/**
 * @file SpanImpl - Universal span implementation.
 * @module @labcraft/core/span
 *
 * Provides cross-platform tracing. ID generation uses `globalThis.crypto`
 * (available in Node.js 15+, all modern browsers, and edge workers).
 * Context propagation uses a best-effort call-stack — correct for sequential
 * and nested spans, which covers all typical frontend usage.
 *
 * For accurate per-request isolation in concurrent Node.js backends, use
 * `runInSpan` from `@labcraft/core/backend`, which uses `AsyncLocalStorage`.
 */

import type { Span, SpanContext, SpanStatus } from './core_telemetry.ts'

////////////////////////////////////////////////////////////////////////////////
// ID Generation
////////////////////////////////////////////////////////////////////////////////

function generateId(bytes: number): string {
  const arr = new Uint8Array(bytes)
  globalThis.crypto.getRandomValues(arr)
  return Array.from(arr, b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Generate a random trace ID (16 bytes = 32 hex chars).
 */
export function generateTraceId(): string {
  return generateId(16)
}

/**
 * Generate a random span ID (8 bytes = 16 hex chars).
 */
export function generateSpanId(): string {
  return generateId(8)
}

////////////////////////////////////////////////////////////////////////////////
// Context Stack
////////////////////////////////////////////////////////////////////////////////

/**
 * Best-effort span context stack.
 *
 * Works correctly for sequential and nested spans. Concurrent independent
 * spans will share the top-of-stack context. Node.js backends should use
 * `runInSpan` from `@labcraft/core/backend` which uses `AsyncLocalStorage`
 * for per-request isolation.
 */
const spanStack: SpanContext[] = []

/**
 * Get the current span context from the stack (if any).
 */
export function getCurrentSpanContext(): SpanContext | undefined {
  return spanStack[spanStack.length - 1]
}

////////////////////////////////////////////////////////////////////////////////
// SpanImpl
////////////////////////////////////////////////////////////////////////////////

/**
 * Universal span implementation.
 *
 * Context inheritance (`traceId`/`parentSpanId`) is passed explicitly by
 * `runInSpan` rather than read from the context store internally, so the
 * same class works with both the stack-based and `AsyncLocalStorage`-based
 * context carriers.
 */
export class SpanImpl implements Span {
  readonly spanId: string
  readonly parentSpanId?: string
  readonly traceId: string
  readonly name: string
  readonly startTime: number

  private readonly attributes = new Map<string, string | number | boolean>()
  private readonly events: Array<{
    name: string
    time: number
    attributes?: Record<string, unknown>
  }> = []

  private _status: SpanStatus = 'ok'
  private statusMessage?: string
  private endTime?: number

  /** Outcome of the span — `'ok'` until {@link setStatus} flips it. */
  get status(): SpanStatus {
    return this._status
  }

  /** Callback invoked when the span ends. */
  private readonly onEnd?: (span: SpanImpl) => void

  constructor(
    name: string,
    options?: {
      traceId?: string
      parentSpanId?: string
      onEnd?: (span: SpanImpl) => void
    },
  ) {
    this.name = name
    this.spanId = generateSpanId()
    this.startTime = performance.now()
    this.traceId = options?.traceId ?? generateTraceId()
    this.parentSpanId = options?.parentSpanId
    this.onEnd = options?.onEnd
  }

  /** Elapsed milliseconds since span start. */
  get duration(): number {
    return (this.endTime ?? performance.now()) - this.startTime
  }

  /** Span context for propagation to child spans. */
  get context(): SpanContext {
    return { traceId: this.traceId, spanId: this.spanId }
  }

  setAttribute(key: string, value: string | number | boolean): void {
    this.attributes.set(key, value)
  }

  addEvent(name: string, attributes?: Record<string, unknown>): void {
    this.events.push({ name, time: performance.now(), attributes })
  }

  setStatus(status: SpanStatus, message?: string): void {
    this._status = status
    this.statusMessage = message
  }

  end(): void {
    if (this.endTime !== undefined) return // Already ended
    this.endTime = performance.now()
    this.onEnd?.(this)
  }

  toJSON(): Record<string, unknown> {
    return {
      traceId: this.traceId,
      spanId: this.spanId,
      parentSpanId: this.parentSpanId,
      name: this.name,
      startTime: this.startTime,
      endTime: this.endTime,
      duration: this.duration,
      status: this.status,
      statusMessage: this.statusMessage,
      attributes: Object.fromEntries(this.attributes),
      events: this.events,
    }
  }
}

////////////////////////////////////////////////////////////////////////////////
// Span Runner
////////////////////////////////////////////////////////////////////////////////

/**
 * Run a function within a span context (stack-based, best-effort).
 *
 * Correct for sequential and nested spans. For concurrent per-request
 * isolation in Node.js, use `runInSpan` from `@labcraft/core/backend`.
 *
 * An optional `onEnd` callback fires when the span closes — used by
 * lifecycle telemetry to feed a duration histogram without spinning up a
 * parallel `timer()`. The callback receives the completed `SpanImpl` so
 * implementations can read `span.duration`, `span.status`, attributes,
 * etc.
 *
 * @example
 * ```typescript
 * const result = await runInSpan('processData', async (span) => {
 *   span.setAttribute('itemCount', items.length)
 *   const processed = await process(items)
 *   span.addEvent('processed')
 *   return processed
 * })
 * ```
 */
export async function runInSpan<T>(
  name: string,
  fn: (span: Span) => Promise<T>,
  onEnd?: (span: SpanImpl) => void,
): Promise<T> {
  const parentContext = getCurrentSpanContext()
  const span = new SpanImpl(name, {
    traceId: parentContext?.traceId,
    parentSpanId: parentContext?.spanId,
    onEnd,
  })
  spanStack.push(span.context)
  try {
    const result = await fn(span)
    span.end()
    return result
  } catch (error) {
    span.setStatus(
      'error',
      error instanceof Error ? error.message : String(error),
    )
    span.end()
    throw error
  } finally {
    spanStack.pop()
  }
}
